import React, { useState, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useUsageLimits } from "@/hooks/useBillingAndUsage";

import {
  type ImportInfluencersModalProps,
  type ParsedInfluencer,
  type ValidationResult,
  ALLOWED_HEADERS,
  REQUIREMENTS_LIST,
  parseInfluencerCsvFile,
  downloadSampleCSV,
  executeInfluencerImport,
  ImportModalHeader,
  ImportDropzone,
  ImportRequirementsCard,
  ImportValidationResults,
  ImportPreviewTable,
  ImportModalFooter,
} from "./influencers/import";

export type { ImportInfluencersModalProps, ParsedInfluencer, ValidationResult };

export function ImportInfluencersModal({
  open,
  onOpenChange,
  onImported,
  onBack,
  campaignId,
}: ImportInfluencersModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<Record<string, unknown>[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { currentOrganization } = useOrganization();
  const { isAtLimit } = useUsageLimits();
  const queryClient = useQueryClient();

  const resetModal = useCallback(() => {
    setFile(null);
    setPreviewData([]);
    setHeaders([]);
    setValidationResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleClose = useCallback(() => {
    onOpenChange(false);
    resetModal();
  }, [onOpenChange, resetModal]);

  const processFile = useCallback((selectedFile: File) => {
    if (!selectedFile.name.toLowerCase().endsWith(".csv")) {
      toast({
        title: "Invalid file type",
        description: "Please select a CSV file.",
        variant: "destructive",
      });
      return;
    }

    setFile(selectedFile);

    parseInfluencerCsvFile(
      selectedFile,
      ({ headers: parsedHeaders, previewData: parsedPreview, validation }) => {
        setHeaders(parsedHeaders);
        setPreviewData(parsedPreview);
        setValidationResult(validation);
      },
      (error) => {
        toast({
          title: "Error parsing CSV",
          description: error.message,
          variant: "destructive",
        });
      }
    );
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) processFile(selectedFile);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) processFile(droppedFile);
  };

  const handleImport = async () => {
    if (!validationResult?.valid.length || !currentOrganization?.id) {
      if (!currentOrganization?.id) {
        toast({
          title: "Error",
          description: "No organization selected. Please select an organization first.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "No valid data to import",
          description: "Please fix validation errors first.",
          variant: "destructive",
        });
      }
      return;
    }

    setIsUploading(true);
    try {
      const success = await executeInfluencerImport({
        validRows: validationResult.valid,
        organizationId: currentOrganization.id,
        campaignId,
        queryClient,
        isAtLimit,
        onSuccess: () => {
          onImported();
          onOpenChange(false);
          resetModal();
        },
      });

      if (!success) {
        // executeInfluencerImport already displayed relevant toasts
      }
    } finally {
      setIsUploading(false);
    }
  };

  // Show loading or error state if no organization
  if (!currentOrganization) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-[500px] p-0 overflow-hidden font-hanken rounded-[16px] border border-slate-100">
          <div className="p-8 text-center font-hanken">
            <p className="text-slate-600 text-sm leading-relaxed">
              Please select an organization first to import influencers.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[960px] p-0 overflow-hidden font-hanken rounded-[24px] border border-slate-100 bg-white shadow-2xl [&>button]:hidden flex flex-col max-h-[90vh]">
        {/* Header Section */}
        <ImportModalHeader onClose={handleClose} onBack={onBack} />

        {/* Scrollable Content Container */}
        <div className="flex-grow overflow-y-auto p-8 space-y-8 scrollbar-thin">
          {/* Two-Column Form Details */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Left Column: Dropzone & Security */}
            <ImportDropzone
              file={file}
              isDragging={isDragging}
              fileInputRef={fileInputRef}
              onFileChange={handleFileChange}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onReset={resetModal}
            />

            {/* Right Column: CSV Format Requirements */}
            <ImportRequirementsCard
              requirementsList={REQUIREMENTS_LIST}
              onDownloadSample={downloadSampleCSV}
            />
          </div>

          {/* Validation Results */}
          {validationResult && (
            <ImportValidationResults validationResult={validationResult} />
          )}

          {/* Preview Section */}
          <ImportPreviewTable
            headers={headers}
            previewData={previewData}
            allowedHeaders={ALLOWED_HEADERS}
          />
        </div>

        {/* Footer Section */}
        <ImportModalFooter
          onClose={handleClose}
          onImport={handleImport}
          isImportDisabled={!validationResult?.valid.length || isUploading}
          isUploading={isUploading}
        />
      </DialogContent>
    </Dialog>
  );
}
